/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useMemo, useEffect, useState } from 'react';
import PlatformList from './PlatformList';
import { PlatformListGroup } from '../../shared/entities/platformListGroup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  ThemeProvider,
  StyledEngineProvider,
  CssBaseline,
  Box,
  Theme,
  Typography,
  IconButton,
  Button,
  adaptV4Theme,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { createTheme } from '@mui/material/styles';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from './FleetMap';
import {
  clearSelectedDatapathUuidInPlatformList,
  closeDetailsPanel,
  closePlatformListDrawer,
  collapseTerminalInPlatformList,
  expandTerminalInPlatformList,
  hideAllTerminalsInPlatformList,
  hideTerminalsInPlatformList,
  openDetailsPanel,
  openPlatformListDrawer,
  setCollapsedTerminalsInPlatformList,
  setSelectedDatapathUuidInPlatformList,
  setPlatformListSortDirection,
  setPlatformListSortField,
  showAllTerminalsInPlatformList,
  showTerminalsInPlatformList,
  setSelectedTerminalTabIndex,
} from '../../store/ui/actions';
import { setMapZoomLatLng, FLEET_MAP_ID } from '../../store/maps/actions';
import Terminal from '../../shared/entities/terminal';
import {
  selectPlatformListDrawerOpen,
  selectHiddenTerminalsList,
  selectSelectedTerminalTabIndex,
  selectPlatformListSortDirection,
  selectPlatformListSortField,
  selectTerminalsAsArray,
  selectTerminals,
  selectCollapsedTerminalsInPlatformList,
  selectIsInitialTerminalsRequestFinished,
  selectPlatformListItems,
  TerminalListItem,
  selectTerminalFilters,
  selectIsTthoEnabled,
  selectSelectedDatapathUuidInFleetView,
} from '../../store';
import { SortDirection } from '../../store/ui/reducer';
import { panToTerminalOnMap } from '../../utils/MapUtils';
import { clearFlightPath, getFlightPathOnce } from '../../store/terminals/actions';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ResourceFilter } from '../common/table/types';
import { shouldRequestFlightPath } from '../../utils/PlatformTypeUtils';
import { isOnline } from '../../utils/ConnectionStatusUtils';
import appTheme from '../common/theme/appTheme.json';

const TOOLBAR_HEIGHT_PX = 204;

const baseTheme = createTheme(
  adaptV4Theme({
    typography: {
      fontFamily: `'Source Sans Pro', sans-serif`,
    },
    palette: {
      primary: { main: '#5EA215' },
      secondary: { main: '#61DED8' },
      background: {
        paper: '#202E39',
        default: '#142431',
      },
      text: { primary: '#ffffff' },
      action: { hover: '#0D1418', selected: '#000000' },
      divider: '#465967',
    },
  }),
);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bulletChar: {
      fontFamily: 'Uni Neue Light',
    },
    visibilityToggleContainer: {
      right: 0,
      paddingRight: '8px',
    },
    visibilityIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
    collapseIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
  }),
);
export const PlatformListContainer = () => {
  // Redux Actions
  const dispatch = useDispatch();

  // Theme and Style
  const classes = useStyles();

  const { height: windowHeight } = useWindowDimensions();

  // Redux Selecters
  const terminalListDrawerOpen = useSelector(selectPlatformListDrawerOpen, shallowEqual);
  const fleetViewHiddenTerminals: string[] = useSelector(selectHiddenTerminalsList, shallowEqual);
  const selectedDatapathUuidFleetView = useSelector(selectSelectedDatapathUuidInFleetView, shallowEqual);
  const selectedTerminalTabIndex = useSelector(selectSelectedTerminalTabIndex, shallowEqual);
  const terminalListSortDirection = useSelector(selectPlatformListSortDirection, shallowEqual);
  const terminalListSortField = useSelector(selectPlatformListSortField, shallowEqual);
  const terminals = useSelector(selectTerminalsAsArray, shallowEqual);
  const terminalMap = useSelector(selectTerminals, shallowEqual);
  const fleetViewCollapsedTerminals = useSelector(selectCollapsedTerminalsInPlatformList, shallowEqual);
  const isInitialTerminalsRequestFinished = useSelector(selectIsInitialTerminalsRequestFinished, shallowEqual);
  const terminalListItems = useSelector(selectPlatformListItems, shallowEqual);
  const fleetViewFilters = useSelector(selectTerminalFilters, shallowEqual);
  const isTthoEnabled = useSelector(selectIsTthoEnabled, shallowEqual);

  useEffect(() => {
    // On Mount
    if (selectedDatapathUuidFleetView && shouldRequestFlightPath(terminalMap[selectedDatapathUuidFleetView])) {
      dispatch(getFlightPathOnce(selectedDatapathUuidFleetView));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const terminalListHeader = useMemo(
    () => (
      <Box height="48px" width={KU_PLUS_KA_DRAWER_WIDTH_PX} position="relative">
        <Box position="absolute" flexGrow={1} left="16px" top="21.5px">
          <Typography component="div">
            <Box fontWeight={300} fontSize="20px" lineHeight="20px">
              <span data-test="platform__list__terminal__count__header">
                {`Terminals (${getNumberOfTerminals(terminalListItems, terminalMap, fleetViewFilters)}`}
              </span>
              <span className={classes.bulletChar}>{` • `}</span>
              <span data-test="platform__list__terminal__online__count">
                {`${getNumberOfOnlineTerminals(terminalListItems, terminalMap, fleetViewFilters)} Online)`}
              </span>
            </Box>
          </Typography>
        </Box>
        <Box position="absolute" top="8px" right="8px">
          <IconButton
            data-test="close-terminal-list-drawer-button"
            onClick={() => {
              dispatch(closePlatformListDrawer());
            }}
          >
            <ChevronLeftIcon sx={{ color: 'white' }} fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    ),
    [terminalListItems, dispatch, fleetViewFilters, terminalMap, classes.bulletChar],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={baseTheme}>
        <CssBaseline />
        {terminalListDrawerOpen && isInitialTerminalsRequestFinished ? (
          <Box position="absolute" width={KU_PLUS_KA_DRAWER_WIDTH_PX}>
            {terminalListHeader}
            <PlatformList
              /* 
                Props 
              */
              // List Settings
              listHeightPx={windowHeight - TOOLBAR_HEIGHT_PX}
              // List Primary Data
              terminalListItems={{ [PlatformListGroup.TERMINAL]: terminalListItems }}
              // List Redux State
              hiddenDatapathUuids={fleetViewHiddenTerminals}
              collapsedDatapathUuids={fleetViewCollapsedTerminals}
              selectedDatapathUuid={selectedDatapathUuidFleetView}
              selectedTerminalTabIndex={selectedTerminalTabIndex}
              terminalListSortDirection={terminalListSortDirection}
              terminalListSortField={terminalListSortField}
              // List Supplemental Data
              terminalMap={terminalMap}
              terminals={{ [PlatformListGroup.TERMINAL]: terminals }}
              hideTtho={!isTthoEnabled}
              /* 
                Click Handlers 
              */
              onCollapseGroupClickedHandler={(event: React.MouseEvent, groupName: string) => {
                event.stopPropagation();
                // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                dispatch(
                  setCollapsedTerminalsInPlatformList(
                    terminals.reduce((prev: any, terminal: Terminal) => ({ ...prev, [terminal.datapathUuid]: true }), {}),
                  ),
                );
              }}
              onExpandGroupClickedHandler={(event: React.MouseEvent, groupName: string) => {
                event.stopPropagation();
                // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                dispatch(setCollapsedTerminalsInPlatformList({}));
              }}
              onHideGroupClickedHandler={(event: React.MouseEvent, groupName: string): void => {
                event.stopPropagation();
                // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                dispatch(hideAllTerminalsInPlatformList());
                dispatch(clearFlightPath());
                dispatch(clearSelectedDatapathUuidInPlatformList());
                dispatch(closeDetailsPanel());
              }}
              onShowGroupClickedHandler={(event: React.MouseEvent, groupName: string): void => {
                event.stopPropagation();
                // Only one group, PlatformListGroup.TERMINALS, is used. Can ignore groupName
                dispatch(showAllTerminalsInPlatformList());
              }}
              onRowClickedHandler={(datapathUuid: string, index: number): void => {
                dispatch(setSelectedTerminalTabIndex(index));
                if (datapathUuid === selectedDatapathUuidFleetView) {
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      terminalMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(FLEET_MAP_ID)(latLngObj));
                      },
                      fleetViewHiddenTerminals,
                    );
                  });
                } else {
                  dispatch(setSelectedDatapathUuidInPlatformList(datapathUuid));
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      terminalMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(FLEET_MAP_ID)(latLngObj));
                      },
                      fleetViewHiddenTerminals,
                    );
                  });
                  dispatch(openDetailsPanel());
                  dispatch(clearFlightPath());
                  if (shouldRequestFlightPath(terminalMap[datapathUuid])) {
                    dispatch(getFlightPathOnce(datapathUuid));
                  }
                }
              }}
              onRowCollapseClickedHandler={(datapathUuid: string): void => {
                const datapathUuidsInGroup = [
                  datapathUuid,
                  ...((terminalMap[datapathUuid] || {}).datapathUuidsOnAircraft || []),
                ];
                if (
                  datapathUuidsInGroup.find((datapathUuidInGroup: string) => fleetViewCollapsedTerminals[datapathUuidInGroup])
                ) {
                  datapathUuidsInGroup.forEach((datapathUuidInGroup: string) => {
                    dispatch(expandTerminalInPlatformList(datapathUuidInGroup));
                  });
                } else {
                  datapathUuidsInGroup.forEach((datapathUuidInGroup: string) => {
                    dispatch(collapseTerminalInPlatformList(datapathUuidInGroup));
                  });
                }
              }}
              onRowHideClickedHandler={(datapathUuid: string): void => {
                const datapathUuids = [datapathUuid, ...((terminalMap[datapathUuid] || {}).datapathUuidsOnAircraft || [])];
                if (fleetViewHiddenTerminals.includes(datapathUuid)) {
                  dispatch(showTerminalsInPlatformList(datapathUuids));
                } else {
                  if (selectedDatapathUuidFleetView && datapathUuids.includes(selectedDatapathUuidFleetView)) {
                    dispatch(clearFlightPath());
                    dispatch(clearSelectedDatapathUuidInPlatformList());
                    dispatch(closeDetailsPanel());
                  }
                  dispatch(hideTerminalsInPlatformList(datapathUuids));
                }
              }}
              onSortColumnHeaderClickedHandler={(
                sortField: string,
                sortDirection: SortDirection,
                groupName: string,
              ): void => {
                dispatch(setPlatformListSortField(sortField));
                dispatch(setPlatformListSortDirection(groupName as PlatformListGroup, sortDirection));
              }}
              onRowSubItemClickedHandler={(datapathUuid: string, tabIndex: number | undefined): void => {
                dispatch(setSelectedTerminalTabIndex(tabIndex));
                if (datapathUuid === selectedDatapathUuidFleetView) {
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      terminalMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(FLEET_MAP_ID)(latLngObj));
                      },
                      fleetViewHiddenTerminals,
                    );
                  });
                } else {
                  dispatch(setSelectedDatapathUuidInPlatformList(datapathUuid));
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      terminalMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(FLEET_MAP_ID)(latLngObj));
                      },
                      fleetViewHiddenTerminals,
                    );
                  });
                  dispatch(openDetailsPanel());
                  dispatch(clearFlightPath());
                  if (shouldRequestFlightPath(terminalMap[datapathUuid])) {
                    dispatch(getFlightPathOnce(datapathUuid));
                  }
                }
              }}
              openListDrawer={openPlatformListDrawer}
              listDrawerOpen={terminalListDrawerOpen}
            />
          </Box>
        ) : (
          <Box maxWidth="32px" position="absolute">
            <Button
              color="primary"
              data-test="open-terminal-list-drawer-button"
              disableRipple
              sx={{
                minHeight: `calc(100vh - 108px)`,
                minWidth: '32px',
                maxWidth: '32px',
                alignItems: 'start',
                paddingTop: '22px',
                '&:hover': {
                  backgroundColor: appTheme.buttonHoverRippleBackgroundColor,
                },
              }}
              onClick={() => {
                dispatch(openPlatformListDrawer());
              }}
            >
              <ChevronRightIcon sx={{ color: appTheme.primaryButtonTextColor }} fontSize="small" />
            </Button>
          </Box>
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

// Counts the number of terminals from the List Item groups as each list item group has 1 or more terminals in it
const getNumberOfTerminals = (
  terminalListItems: TerminalListItem[],
  terminalMap: { [datapathUuid: string]: Terminal },
  fleetViewFilters: ResourceFilter[],
) => {
  return terminalListItems.reduce(
    (numberOfTerminals: number, currentTerminalListItem: TerminalListItem) =>
      numberOfTerminals +
      currentTerminalListItem.datapathUuidsInGroup.filter((datapathUuid: string) =>
        fleetViewFilters.every(({ key, value }) => (terminalMap[datapathUuid] || {})[key] === value),
      ).length,
    0,
  );
};

const getNumberOfOnlineTerminals = (
  terminalListItems: TerminalListItem[],
  terminalMap: { [datapathUuid: string]: Terminal },
  fleetViewFilters: ResourceFilter[],
) => {
  return terminalListItems.reduce(
    (numberOfTerminals: number, currentTerminalListItem: TerminalListItem) =>
      numberOfTerminals +
      currentTerminalListItem.datapathUuidsInGroup.filter(
        (datapathUuid: string) =>
          fleetViewFilters.every(({ key, value }) => (terminalMap[datapathUuid] || {})[key] === value) &&
          isOnline(terminalMap[datapathUuid].connectivityStatus),
      ).length,
    0,
  );
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}
